import React, { useMemo } from 'react';
import { DropDown, Label, SelectItem } from '@zaiusinc/hera';
import { Button } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import styles from './DataSyncFormSection.module.sass';
import Notification from '../Notification';
import { LocaleConfig } from '../../../../types/Schema';
import { FilterConfig } from '../../../../types/DataSyncItem';
import {
  expressionToLanguage,
  languageToExpression,
} from '../../../../lib/filterExpression';
import { localeLabel } from '../../../../lib/localeCatalog';

/**
 * Sentinel value for an existing expression that this dropdown cannot
 * round-trip (e.g. compound expressions, custom shapes). Surfaced as a disabled
 * option so the user knows a custom filter is in effect rather than seeing a
 * silent default while the underlying value is preserved.
 */
export const CUSTOM_EXPRESSION_SENTINEL = '__CUSTOM_EXPRESSION__';

const FIELD_LANGUAGE = 'language';

const fieldItems: SelectItem[] = [
  { text: 'Language', value: FIELD_LANGUAGE },
];

export interface FiltersSectionProps {
  localeConfig?: LocaleConfig;
  value?: FilterConfig;
  onChange: (value: FilterConfig | undefined) => void;
  disabled?: boolean;
  loading?: boolean;
}

const FiltersSection: React.FC<FiltersSectionProps> = ({
  localeConfig,
  value,
  onChange,
  disabled = false,
  loading = false,
}) => {
  const supportedLocales = localeConfig?.supported_locales;

  const parsedCode = value ? expressionToLanguage(value.expression) : undefined;
  const hasUnparseableExpression = value !== undefined && parsedCode === undefined;
  const isLanguageFilterAdded = value !== undefined;

  // The selected language parses cleanly but the source app schema no longer
  // declares it in supported_locales (e.g. the app dropped locale support after
  // this sync was configured). Surface it rather than silently showing a value
  // that maps to no available option. Suppressed while loading, when
  // supported_locales is not yet populated and would trigger a false positive.
  const isSelectedLocaleUnavailable =
    !loading && parsedCode !== undefined && !(supportedLocales ?? []).includes(parsedCode);

  const localeItems: SelectItem[] = useMemo(() => {
    const items: SelectItem[] = (supportedLocales ?? []).map((code) => ({
      text: localeLabel(code),
      value: code,
    }));
    if (isSelectedLocaleUnavailable && parsedCode) {
      items.push({
        text: `${localeLabel(parsedCode)} (no longer available)`,
        value: parsedCode,
        disabled: true,
        disabledTooltip:
          'This language is no longer available in the source app schema. ' +
          'Create a new sync with an available language.',
      });
    }
    if (hasUnparseableExpression) {
      items.push({
        text: 'Custom filter expression',
        value: CUSTOM_EXPRESSION_SENTINEL,
        disabled: true,
        disabledTooltip:
          'This sync has a filter expression that was set outside this dropdown. ' +
          'Selecting another option will replace it.',
      });
    }
    return items;
  }, [supportedLocales, hasUnparseableExpression, isSelectedLocaleUnavailable, parsedCode]);

  if ((!supportedLocales || supportedLocales.length === 0) && !isLanguageFilterAdded) {
    return null;
  }

  const handleSelectLocale = (item: SelectItem) => {
    if (item.value === CUSTOM_EXPRESSION_SENTINEL) {
      return;
    }
    onChange({ expression: languageToExpression(item.value as string) });
  };

  const handleAddLanguageFilter = () => {
    // Only reachable when the "Add filter" button is shown, which requires a
    // non-empty supportedLocales, so index 0 is always defined here.
    if (!supportedLocales || supportedLocales.length === 0) {
      return;
    }
    onChange({ expression: languageToExpression(supportedLocales[0]) });
  };

  const handleRemoveLanguageFilter = () => {
    onChange(undefined);
  };

  const selectedLocaleValue = parsedCode ?? CUSTOM_EXPRESSION_SENTINEL;
  const canAddMoreFilters = !isLanguageFilterAdded;

  return (
    <section data-testid="filters-section">
      <Label label="Filters" nonInteractive={true}>
        {isLanguageFilterAdded && (
          <div className={styles.filterRow} data-testid="language-filter-row">
            <DropDown
              items={fieldItems}
              value={FIELD_LANGUAGE}
              onItemSelect={() => undefined}
              width={180}
              disabled={disabled}
            />
            <span className={styles.filterRowOperator}>=</span>
            <DropDown
              items={localeItems}
              value={selectedLocaleValue}
              onItemSelect={handleSelectLocale}
              width={180}
              disabled={disabled}
            />
            {!disabled && (
              <Button
                minimal={true}
                icon={IconNames.CROSS}
                onClick={handleRemoveLanguageFilter}
                aria-label="Remove filter"
                data-testid="remove-language-filter"
              />
            )}
          </div>
        )}

        <Notification
          message={
            'The language this sync is configured for is no longer available ' +
            'in the source app schema. Create a new sync with an available language.'
          }
          show={isSelectedLocaleUnavailable}
          type="warning"
        />

        {canAddMoreFilters && (
          <Button
            icon={IconNames.PLUS}
            text="Add filter"
            minimal={true}
            onClick={handleAddLanguageFilter}
            data-testid="add-filter-button"
            disabled={disabled}
          />
        )}
      </Label>
    </section>
  );
};

export default FiltersSection;
